import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CateringOrderDraft } from '../../../../core/models/catering.model';
import { LocaleService } from '../../../../core/services/locale.service';
import { formatCurrencyMinor } from '../../../../core/utils/catering';
import { withClock } from '../../../../core/utils/format-prefs';
import { isVendorType, VendorType } from '../../../../core/constants/enums';
import { PaperIconComponent, PaperButtonComponent } from '@ricoh-paper/angular';

/**
 * ServiceCardComponent — rich catering order card shown in the booking form.
 *
 * Shows vendor logo/initial, name, item summary with individual items listed,
 * total cost, delivery time, and edit/remove actions.
 */
@Component({
  selector: 'ct-service-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent, PaperButtonComponent, TranslocoPipe],
  template: `
    <div class="sc">
      <!-- Header: logo + vendor + total + actions -->
      <div class="sc__header">
        <div class="sc__logo">
          @if (draft().vendorLogo) {
            <img class="sc__logo-img" [src]="draft().vendorLogo" [alt]="draft().vendorName"
                 (error)="$any($event.target).style.display='none'" />
          } @else if (isVendorType(draft().vendorType, VendorType.EMAIL_ONLY)) {
            <paper-icon name="email" [size]="14" aria-hidden="true" />
          } @else {
            <span>{{ draft().vendorName.charAt(0) }}</span>
          }
        </div>
        <div class="sc__vendor-info">
          <span class="sc__vendor-name">{{ draft().vendorName }}</span>
          <span class="sc__delivery">
            <paper-icon name="clock" [size]="10" aria-hidden="true" />
            {{ formattedTime() }}
          </span>
        </div>
        @if (dayLabel()) {
          <span class="sc__day-badge">{{ dayLabel() }}</span>
        }
        <div class="sc__total">{{ formattedTotal() }}</div>
        <div class="sc__actions">
          <paper-button intent="ghost" size="xs" shape="icon" icon="edit"
                     [ariaLabel]="'catering.customer.service_card.edit_order' | transloco"
                     (click)="edit.emit(); $event.stopPropagation()" />
          <paper-button intent="ghost" size="xs" shape="icon" icon="close"
                     [ariaLabel]="'catering.customer.service_card.remove_order' | transloco"
                     (click)="remove.emit(); $event.stopPropagation()" />
        </div>
      </div>

      <!-- Item list preview -->
      @if (draft().items.length > 0) {
        <div class="sc__items">
          @for (item of draft().items.slice(0, 4); track item.cartLineId || item.menuItemId) {
            <div class="sc__item-row">
              <span class="sc__item-qty">{{ item.quantity }}×</span>
              <span class="sc__item-name">{{ item.name }}</span>
              <span class="sc__item-price">{{ fmtPrice(item.totalCostPence) }}</span>
            </div>
          }
          @if (draft().items.length > 4) {
            <span class="sc__more">{{ 'catering.customer.service_card.more_items' | transloco: { count: draft().items.length - 4 } }}</span>
          }
        </div>
      } @else if (draft().freeTextRequest) {
        <div class="sc__items">
          <p class="sc__free-text">{{ draft().freeTextRequest!.slice(0, 100) }}{{ draft().freeTextRequest!.length > 100 ? '...' : '' }}</p>
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }

    .sc {
      background: var(--surface-2);
      border: 1px solid var(--border-low);
      border-radius: var(--radius-sm);
      overflow: hidden;
      margin: 6px 0;
    }

    /* ── Header ────────────────────── */

    .sc__header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 10px 8px;
    }

    .sc__logo {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      background: var(--color-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: 14px;
      font-weight: 700;
      color: var(--text-on-accent);
      overflow: hidden;

      paper-icon { color: currentColor; }
    }

    .sc__logo-img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      background: var(--surface-logo-canvas);
      padding: 3px;
    }

    .sc__vendor-info {
      flex: 1;
      min-width: 0;
    }

    .sc__vendor-name {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: var(--text);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .sc__delivery {
      display: flex;
      align-items: center;
      gap: 3px;
      font-size: 10px;
      color: var(--text-tertiary);
      font-variant-numeric: tabular-nums;
      margin-top: 1px;
    }

    .sc__day-badge {
      font-size: 10px; /* type-scale floor */
      font-weight: 700;
      padding: 2px 6px;
      border-radius: var(--radius-pill);
      background: var(--color-primary-bg);
      color: var(--color-primary-text);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .sc__total {
      font-size: 14px;
      font-weight: 700;
      color: var(--text);
      font-variant-numeric: tabular-nums;
      flex-shrink: 0;
    }

    .sc__actions {
      display: flex;
      gap: 2px;
      flex-shrink: 0;
    }

    /* ── Items ─────────────────────── */

    .sc__items {
      padding: 0 10px 8px;
    }

    .sc__item-row {
      display: flex;
      align-items: baseline;
      gap: 4px;
      padding: 2px 0;
      font-size: 11px;
    }

    .sc__item-qty {
      color: var(--text-tertiary);
      font-variant-numeric: tabular-nums;
      flex-shrink: 0;
      width: 20px;
    }

    .sc__item-name {
      flex: 1;
      color: var(--text-secondary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .sc__item-price {
      color: var(--text-tertiary);
      font-variant-numeric: tabular-nums;
      flex-shrink: 0;
    }

    .sc__more {
      display: block;
      font-size: 10px;
      color: var(--text-tertiary);
      padding-top: 2px;
    }

    .sc__free-text {
      font-size: 11px;
      color: var(--text-secondary);
      font-style: italic;
      margin: 0;
      line-height: 1.4;
    }

  `],
})
export class ServiceCardComponent {
  readonly draft = input.required<CateringOrderDraft>();
  readonly edit = output<void>();
  readonly remove = output<void>();
  protected readonly isVendorType = isVendorType;
  protected readonly VendorType = VendorType;

  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  protected fmtPrice(pence: number): string {
    return formatCurrencyMinor(pence, this.draft().currency || 'GBP');
  }

  protected readonly formattedTotal = computed(() =>
    formatCurrencyMinor(this.draft().totalCostPence, this.draft().currency || 'GBP'),
  );

  protected readonly formattedTime = computed(() => {
    const dt = this.draft().deliveryTime;
    if (!dt) return '';
    return dt.toLocaleTimeString(this.locale.locale(), withClock({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }));
  });

  protected readonly dayLabel = computed(() => {
    this.locale.locale();
    const idx = this.draft().dayIndex;
    return idx != null
      ? this.transloco.translate('catering.customer.service_card.day_label', { day: idx + 1 })
      : null;
  });
}
